import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

/**
 * Discount + expiration + management-fee picker shown before generating a
 * product PDF. Everything here only affects the generated document — never
 * persisted, never reflected on the public site.
 * `onGenerate(discount, expirationDate, managementFeeLabel)` is called with
 * discount = null when "Aucune remise" is selected, and managementFeeLabel
 * = '' when the field is left empty (no fee note shown in the document).
 */
export default function PdfExportDialog({ open, onClose, onGenerate, generating, defaultManagementFeeLabel = '' }) {
    const [discountType, setDiscountType] = useState('none');
    const [discountValue, setDiscountValue] = useState('');
    const [expiration, setExpiration] = useState(null);
    const [managementFeeLabel, setManagementFeeLabel] = useState('');

    // Re-sync to this product's stored fee note each time the dialog opens
    // (it stays mounted between exports, so the field would otherwise keep
    // whatever the previous product left in it).
    useEffect(() => {
        if (open) setManagementFeeLabel(defaultManagementFeeLabel || '');
    }, [open, defaultManagementFeeLabel]);

    const today = dayjs().startOf('day');
    const maxDate = today.add(3, 'month');

    const hasDiscount = discountType !== 'none' && Number(discountValue) > 0;
    const canGenerate = !hasDiscount || (expiration != null && !expiration.isBefore(today) && !expiration.isAfter(maxDate));

    const handleTypeChange = (_event, value) => {
        if (!value) return;
        setDiscountType(value);
        if (value === 'none') {
            setDiscountValue('');
            setExpiration(null);
        }
    };

    const handleGenerate = () => {
        const discount = hasDiscount ? { type: discountType, value: Number(discountValue) } : null;
        onGenerate(discount, hasDiscount ? expiration.toDate() : null, managementFeeLabel.trim());
    };

    const handleClose = () => {
        if (generating) return;
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Exporter en PDF</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        Remise optionnelle, appliquée uniquement à ce document — le prix affiché
                        sur le site n'est pas modifié.
                    </Typography>

                    <ToggleButtonGroup exclusive fullWidth value={discountType} onChange={handleTypeChange}>
                        <ToggleButton value="none">Aucune remise</ToggleButton>
                        <ToggleButton value="percent">Pourcentage</ToggleButton>
                        <ToggleButton value="fixed">Montant fixe</ToggleButton>
                    </ToggleButtonGroup>

                    {discountType !== 'none' && (
                        <TextField
                            label={discountType === 'percent' ? 'Remise (%)' : 'Remise (€)'}
                            type="number"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(e.target.value)}
                            size="small"
                            fullWidth
                            slotProps={{
                                htmlInput: discountType === 'percent' ? { min: 1, max: 100 } : { min: 1 },
                            }}
                        />
                    )}

                    {hasDiscount && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Date d'expiration de l'offre"
                                value={expiration}
                                onChange={setExpiration}
                                minDate={today}
                                maxDate={maxDate}
                                slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                            />
                        </LocalizationProvider>
                    )}

                    {hasDiscount && (
                        <Alert severity="info" variant="outlined">
                            Date d'expiration limitée à 3 mois maximum ({maxDate.format('DD/MM/YYYY')}).
                        </Alert>
                    )}

                    <Divider />

                    <TextField
                        label="Frais de gestion affichés dans le document"
                        placeholder="Ex : Frais de dossier 15 %"
                        value={managementFeeLabel}
                        onChange={(e) => setManagementFeeLabel(e.target.value)}
                        size="small"
                        fullWidth
                        helperText="Laisser vide pour ne rien afficher. Ne modifie que ce PDF, pas la fiche du produit."
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={generating}>Annuler</Button>
                <Button
                    variant="contained"
                    startIcon={<PictureAsPdfIcon />}
                    disabled={!canGenerate || generating}
                    onClick={handleGenerate}
                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                >
                    {generating ? 'Génération...' : 'Générer le PDF'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
